import { CatalogAddress } from '@/types/Catalog';
import { Seller } from '@/types/Seller';

import capitalize from './capitalize';
import { CanadianProvincesWithISOCodes, CountriesWithISOCodes, USStatesWithISOCodes } from './intl';
import { translateAccents } from './translateCharacterAccents';

export { CanadianProvincesWithISOCodes };

export const isFromUS = (country?: string) => {
    if (!country) {
        return false;
    }
    const lower = country.toLowerCase().replace(/\s+/g, '').trim();
    return lower === 'unitedstates' || lower === 'usa' || lower === 'us';
};

export const translateAddressAccents = (form: any) => {
    if (!form) {
        return form;
    }
    return {
        address: translateAccents(form.address),
        address1: translateAccents(form.address1),
        address2: translateAccents(form.address2),
        city: translateAccents(form.city),
        country: translateAccents(form.country),
        firstName: translateAccents(form.firstName),
        lastName: translateAccents(form.lastName),
        mobile: translateAccents(form.mobile),
        mobileNumber: translateAccents(form.mobileNumber),
        organizationName: translateAccents(form.organizationName),
        state: translateAccents(form.state),
        zip: translateAccents(form.zip),
        zipcode: translateAccents(form.zipcode),
    };
};

export const getAddressForCatalog = (catalogAddress: CatalogAddress, seller: Seller) => {
    if (catalogAddress?.address1) {
        const {
            address1 = '',
            address2 = '',
            city = '',
            country = 'US',
            fullCountryName = 'United States',
            lat,
            lng,
            postalCode = '',
            state = '',
        } = catalogAddress;
        return {
            address1,
            address2,
            city: city?.trim() || '',
            country,
            fullCountryName,
            lat,
            lng,
            postalCode,
            state,
        } as CatalogAddress;
    } else {
        const {
            address = '',
            address2 = '',
            city = '',
            country = 'United States',
            countryCode = 'US',
            stateCode = '',
            zip,
        } = seller || {};
        const { lat, lng } = catalogAddress || {};
        return {
            address1: address,
            address2,
            city: city?.trim() || '',
            country: countryCode,
            fullCountryName: country,
            lat,
            lng,
            postalCode: zip,
            state: stateCode,
        } as CatalogAddress;
    }
};

export const getBidderReviewLocation = (city = '', state = '', country = '') => {
    const newCity =
        city.length > 3
            ? (' ' + city)
                  .toLowerCase()
                  .replace(/ (\w)/g, (str) => {
                      return str.toUpperCase();
                  })
                  .trim()
            : city.toUpperCase();
    const newState =
        state.length > 3
            ? (' ' + state)
                  .toLowerCase()
                  .replace(/ (\w)/g, (str) => {
                      return str.toUpperCase();
                  })
                  .trim()
            : state.toUpperCase();
    const newCountry =
        country.length > 3
            ? (' ' + country)
                  .toLowerCase()
                  .replace(/ (\w)/g, (str) => {
                      return str.toUpperCase();
                  })
                  .trim()
            : country.toUpperCase();

    let returnString = '';
    if (isFromUS(newCountry) && Boolean(newCity) && Boolean(newState)) {
        returnString = `${newCity}, ${newState}`;
    } else if (Boolean(newCity) && Boolean(newCountry)) {
        returnString = `${newCity}, ${newCountry}`;
    }
    return returnString;
};

export const get2DigitCountryCode = (country: string): string => {
    if (!country || country.length === 2) {
        return country?.toUpperCase();
    }
    const newCountry = CountriesWithISOCodes.find(
        (c) =>
            c.code === country.toUpperCase() ||
            c.code3 === country.toUpperCase() ||
            c.name.toUpperCase() === country.toUpperCase()
    );
    if (!newCountry) {
        return country;
    }
    return newCountry?.code || '';
};

export const getCountryName = (country: string): string => {
    if (!country || country.length > 3) {
        return country;
    }
    const newCountry = CountriesWithISOCodes.find(
        (c) =>
            c.code === country.toUpperCase() ||
            c.code3 === country.toUpperCase() ||
            c.name.toUpperCase() === country.toUpperCase()
    );
    if (!newCountry) {
        return country;
    }
    return newCountry?.name || '';
};

export const getStateName = (state: string): string => {
    if (!state || state.length > 2) {
        return state;
    }
    const newState = USStatesWithISOCodes.find(
        (c) => c.code === state.toUpperCase() || c.name.toUpperCase() === state.toUpperCase()
    );
    if (!newState) {
        return state;
    }
    return newState?.name || '';
};

const fixYellingNames = (name = ''): string => {
    if (name === (name || '').toUpperCase()) {
        return capitalize((name || '').toLowerCase());
    }
    return name;
};

export const getStateCode = (state: string): string => {
    if (typeof state === 'string' && state.length === 2) {
        return state.toUpperCase();
    } else if (typeof state !== 'string') {
        return '';
    }
    const newState = USStatesWithISOCodes.find(
        (c) => c.code === state.toUpperCase() || c.name.toUpperCase() === state.toUpperCase()
    );
    if (!newState) {
        return state;
    }
    return newState?.code || '';
};

export const getShortAddressFormat = (city = '', countryCode = '', stateCode = ''): string => {
    let state = getStateCode(stateCode).toLocaleUpperCase();
    if (state.length > 2) {
        state = capitalize(state.toLocaleLowerCase());
    }
    const locationArray = [city, state, countryCode.toLocaleUpperCase()];
    return locationArray.filter(Boolean).join(', ');
};

export const getLongAddressFormat = (city = '', country = '', stateCode = ''): string => {
    let state = getStateCode(stateCode).toLocaleUpperCase();
    if (state.length > 2) {
        state = capitalize(state.toLocaleLowerCase());
    }
    const locationArray = [city, state, fixYellingNames(country)];
    return locationArray.filter(Boolean).join(', ');
};

export const US_POSTAL_CODE_LENGTH = 5;
/** Matches US ZIP codes: 5-digit (35186) or ZIP+4 (35186-6936) */
export const US_ZIP_CODE_REGEX = /^\d{5}(-\d{4})?$/;
